import { ThemeEvents, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const PROPERTY_GROUP = '_bitro_fbt_group';
const PROPERTY_SOURCE = '_bitro_fbt_source_product_id';
const PROPERTY_ROLE = '_bitro_fbt_role';

class BitroBoughtTogether extends HTMLElement {
  #connected = false;

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;

    this.addEventListener('change', this.#handleChange);
    this.addEventListener('submit', this.#handleSubmit);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleCurrentVariantUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);

    for (const select of this.querySelectorAll('[data-bitro-bought-together-variant]')) {
      if (select instanceof HTMLSelectElement) this.#syncAddOnVariant(select);
    }
    this.#syncView();
  }

  disconnectedCallback() {
    if (!this.#connected) return;
    this.#connected = false;

    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('submit', this.#handleSubmit);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleCurrentVariantUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  #handleChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches('[data-bitro-bought-together-variant]')) {
      this.#syncAddOnVariant(target);
    } else if (!(target instanceof HTMLInputElement) || !target.matches('[data-bitro-bought-together-item]')) {
      return;
    }

    this.#setStatus('');
    this.#syncView();
  };

  #handleCurrentVariantUpdate = (event) => {
    const sourceProductId = String(this.dataset.sourceProductId || '');
    const eventProductId = String(
      event?.detail?.data?.newProduct?.id || event?.detail?.data?.productId || ''
    );
    if (!sourceProductId || eventProductId !== sourceProductId) return;

    const variant = event?.detail?.resource;
    const variantId = Number(variant?.id || 0);
    const variantPrice = Number(variant?.price);
    if (Number.isSafeInteger(variantId) && variantId > 0) {
      this.dataset.currentVariantId = String(variantId);
    }
    if (Number.isFinite(variantPrice) && variantPrice >= 0) {
      this.dataset.currentVariantPrice = String(variantPrice);
    }
    this.#syncView();
  };

  #handlePageShow = () => {
    const button = this.querySelector('[data-bitro-bought-together-submit]');
    if (button instanceof HTMLButtonElement) this.#setLoading(button, false);
    this.#syncView();
  };

  #syncAddOnVariant(select) {
    const option = select.selectedOptions[0];
    const item = select.closest('.bitro-bought-together__item');
    if (!(option instanceof HTMLOptionElement) || !(item instanceof HTMLElement)) return;

    const checkbox = item.querySelector('[data-bitro-bought-together-item]');
    const variantId = Number(option.value);
    const price = Number(option.dataset.price || 0);
    if (checkbox instanceof HTMLInputElement) {
      checkbox.value = Number.isSafeInteger(variantId) && variantId > 0 ? String(variantId) : '';
      checkbox.dataset.price = Number.isFinite(price) && price >= 0 ? String(price) : '0';
    }

    const priceElement = item.querySelector('[data-bitro-bought-together-price]');
    if (priceElement instanceof HTMLElement) {
      priceElement.textContent = this.#formatMoney(price);
    }

    const image = item.querySelector('.bitro-bought-together__image');
    if (image instanceof HTMLImageElement && option.dataset.imageUrl) {
      image.src = option.dataset.imageUrl;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.alt = option.dataset.imageAlt || image.alt;
    }

    if (option.dataset.variantUrl) {
      for (const link of item.querySelectorAll('[data-bitro-bought-together-product-link]')) {
        if (link instanceof HTMLAnchorElement) link.href = option.dataset.variantUrl;
      }
    }
  }

  #selectedAddOns() {
    return Array.from(this.querySelectorAll('[data-bitro-bought-together-item]:checked'))
      .map((input) => ({
        id: Number(input.value),
        price: Number(input.dataset.price || 0),
        productId: Number(input.dataset.productId || 0),
        quantity: 1,
        role: 'addon',
      }))
      .filter(
        (item) =>
          Number.isSafeInteger(item.id) &&
          item.id > 0 &&
          Number.isFinite(item.price) &&
          item.price >= 0 &&
          Number.isSafeInteger(item.productId) &&
          item.productId > 0
      );
  }

  #sourceProductId() {
    const value = String(this.dataset.sourceProductId || '');
    return /^[1-9]\d{0,19}$/.test(value) ? value : '';
  }

  #discountPercent() {
    const value = Number(this.dataset.bundleDiscountPercent || 0);
    return Number.isFinite(value) ? Math.min(30, Math.max(0, value)) : 0;
  }

  #discountMinimum() {
    const value = Number(this.dataset.bundleDiscountMinItems || 2);
    return Number.isFinite(value) ? Math.min(6, Math.max(2, Math.round(value))) : 2;
  }

  #currentVariantId() {
    const input = document.querySelector('product-form-component input[name="id"]');
    const formValue = input instanceof HTMLInputElement ? Number(input.value) : 0;
    if (Number.isSafeInteger(formValue) && formValue > 0) return formValue;

    const fallback = Number(this.dataset.currentVariantId || 0);
    return Number.isSafeInteger(fallback) && fallback > 0 ? fallback : 0;
  }

  #currentVariantPrice() {
    const value = Number(this.dataset.currentVariantPrice || 0);
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }

  #currentQuantity() {
    const input = document.querySelector(
      'product-form-component quantity-selector-component input[name="quantity"]'
    );
    const value = input instanceof HTMLInputElement ? Number(input.value) : 1;
    return Number.isSafeInteger(value) && value > 0 ? value : 1;
  }

  #includesCurrentProduct() {
    return this.dataset.includeCurrentProduct === 'true';
  }

  #formatMoney(cents) {
    const safeCents = Number.isFinite(cents) && cents >= 0 ? cents : 0;
    return formatMoney(
      safeCents,
      this.dataset.moneyFormat || '\u20ac{{amount_with_comma_separator}}',
      this.dataset.currencyCode || 'EUR'
    );
  }

  #pricing() {
    const addOns = this.#selectedAddOns();
    const originalAddOnTotal = addOns.reduce((sum, item) => sum + item.price, 0);
    const distinctProducts = new Set(addOns.map((item) => item.productId)).size;
    const percent = this.#discountPercent();
    const active =
      Boolean(this.#sourceProductId()) &&
      percent > 0 &&
      distinctProducts >= this.#discountMinimum();
    const savings = active
      ? addOns.reduce((sum, item) => sum + Math.floor((item.price * percent) / 100), 0)
      : 0;
    const currentProductTotal = this.#includesCurrentProduct() ? this.#currentVariantPrice() : 0;

    return {
      active,
      distinctProducts,
      originalTotal: currentProductTotal + originalAddOnTotal,
      savings,
      total: Math.max(0, currentProductTotal + originalAddOnTotal - savings),
    };
  }

  #syncView() {
    const button = this.querySelector('[data-bitro-bought-together-submit]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = this.#selectedAddOns().length === 0 || button.dataset.loading === 'true';
    }

    const pricing = this.#pricing();
    const total = this.querySelector('[data-bitro-bought-together-total]');
    if (total instanceof HTMLElement) total.textContent = this.#formatMoney(pricing.total);

    const original = this.querySelector('[data-bitro-bought-together-original]');
    if (original instanceof HTMLElement) {
      original.textContent = this.#formatMoney(pricing.originalTotal);
      original.hidden = !pricing.active;
    }

    const savings = this.querySelector('[data-bitro-bought-together-savings]');
    if (savings instanceof HTMLElement) {
      savings.textContent = 'Du sparst ' + this.#formatMoney(pricing.savings);
      savings.hidden = !pricing.active;
    }

    const hint = this.querySelector('[data-bitro-bought-together-hint]');
    if (hint instanceof HTMLElement) {
      const remaining = Math.max(0, this.#discountMinimum() - pricing.distinctProducts);
      hint.textContent =
        'Noch ' +
        remaining +
        ' Artikel ausw\u00e4hlen f\u00fcr ' +
        this.#discountPercent() +
        ' % Rabatt.';
      hint.hidden = this.#discountPercent() <= 0 || pricing.active;
    }

    const pricingRegion = this.querySelector('[data-bitro-bought-together-pricing]');
    if (pricingRegion instanceof HTMLElement) {
      pricingRegion.dataset.discountActive = pricing.active ? 'true' : 'false';
    }
  }

  #createGroupId() {
    const sourceProductId = this.#sourceProductId();
    if (!sourceProductId) return '';

    const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const bytes = new Uint8Array(10);
    if (globalThis.crypto?.getRandomValues) {
      globalThis.crypto.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }
    const random = Array.from(bytes, (value) => alphabet[value % alphabet.length]).join('');
    return 'fbt_' + sourceProductId + '_' + Date.now().toString(36) + '_' + random;
  }

  #cartPayloadItems(items, discountActive) {
    if (!discountActive) {
      return items.map(({ id, quantity }) => ({ id, quantity }));
    }

    const group = this.#createGroupId();
    const sourceProductId = this.#sourceProductId();
    if (!group || !sourceProductId) {
      return items.map(({ id, quantity }) => ({ id, quantity }));
    }

    return items.map(({ id, quantity, role }) => {
      if (role !== 'addon') return { id, quantity };
      return {
        id,
        quantity,
        properties: {
          [PROPERTY_GROUP]: group,
          [PROPERTY_SOURCE]: sourceProductId,
          [PROPERTY_ROLE]: 'addon',
        },
      };
    });
  }

  #setStatus(message, isError = false) {
    const status = this.querySelector('[data-bitro-bought-together-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    status.dataset.error = isError ? 'true' : 'false';
  }

  #setLoading(button, loading) {
    button.dataset.loading = loading ? 'true' : 'false';
    button.disabled = loading || this.#selectedAddOns().length === 0;
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    button.textContent = loading
      ? button.dataset.loadingText || button.textContent
      : button.dataset.defaultText || button.textContent;
  }

  #handleSubmit = async (event) => {
    event.preventDefault();

    const button = this.querySelector('[data-bitro-bought-together-submit]');
    const addOns = this.#selectedAddOns();
    if (!(button instanceof HTMLButtonElement) || addOns.length === 0) return;

    this.#setLoading(button, true);
    this.#setStatus('');
    let redirecting = false;

    try {
      const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!cartResponse.ok) {
        throw new Error('Der Warenkorb konnte nicht geladen werden.');
      }
      const cart = await cartResponse.json();
      const currentVariantId = this.#includesCurrentProduct() ? this.#currentVariantId() : 0;
      const currentAlreadyInCart =
        currentVariantId > 0 &&
        cart?.items?.some((item) => Number(item.variant_id) === currentVariantId);

      const items = [...addOns];
      if (currentVariantId > 0 && !currentAlreadyInCart) {
        items.unshift({
          id: currentVariantId,
          quantity: this.#currentQuantity(),
          role: 'anchor',
        });
      }

      const pricing = this.#pricing();
      const payload = {
        items: this.#cartPayloadItems(items, pricing.active),
      };

      const response = await fetch(globalThis.Theme?.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text().catch(() => '');
      let data = {};
      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          if (!response.ok) data = { message: responseText };
        }
      }

      if (!response.ok || data.status) {
        const message =
          [data.description, data.message].find((value) => typeof value === 'string' && value.trim()) ||
          'Die Produkte konnten nicht hinzugef\u00fcgt werden.';
        document.dispatchEvent(new CartErrorEvent(this.id, message, message, {}));
        this.#setStatus(message, true);
        return;
      }

      const root = String(globalThis.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const cartUrl = globalThis.Theme?.routes?.cart_url || root + 'cart';
      window.location.assign(cartUrl);
      redirecting = true;
    } catch (error) {
      console.error(error);
      const message = 'Die Produkte konnten nicht hinzugef\u00fcgt werden.';
      document.dispatchEvent(new CartErrorEvent(this.id, message, message, {}));
      this.#setStatus(message, true);
    } finally {
      if (!redirecting) {
        this.#setLoading(button, false);
        this.#syncView();
      }
    }
  };
}

if (!customElements.get('bitro-bought-together')) {
  customElements.define('bitro-bought-together', BitroBoughtTogether);
}
